import { FC, type ReactNode } from "react";
import { HomeSeriesPagination } from "../../components/common";
import {
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Popconfirm, Select, type SelectProps } from "antd";
import type { HomeScopeOption, HomeTimeSortOption } from "./useHomeSeriesSurface";
import type { HomeSeriesFolderItem } from "./types";
import "./homeSeriesContent.scss";

type HomeToolbarSelectProps = Omit<SelectProps, "popupClassName"> & {
  widthPreset?: "time" | "scope";
  popupClassName?: string;
};

/** 工具栏筛选：样式在 homeSeriesContent.scss（避免 Tailwind 任意选择器未生成或被 antd 覆盖）；其它页（如剧集管理）可复用。 */
export const HomeToolbarSelect: FC<HomeToolbarSelectProps> = (props) => {
  const {
    className,
    widthPreset,
    popupClassName,
    suffixIcon,
    getPopupContainer,
    ...rest
  } = props;
  const presetClass =
    widthPreset === "time"
      ? "home-series__filter-select--time"
      : widthPreset === "scope"
        ? "home-series__filter-select--scope"
        : "";
  const rootClass = ["home-series__filter-select", presetClass, className].filter(Boolean).join(" ");
  const popupCls = [popupClassName].filter(Boolean).join(" ") || undefined;
  const defaultSuffix: ReactNode = (
    <DownOutlined style={{ fontSize: 11, color: "#94a3b8" }} />
  );
  return (
    <Select
      className={rootClass}
      popupClassName={popupCls}
      suffixIcon={suffixIcon ?? defaultSuffix}
      getPopupContainer={getPopupContainer ?? ((n) => n.parentElement ?? document.body)}
      {...rest}
    />
  );
};

const SERIES_FOLDER_THUMB_URL = "/img/series-folder.png";

const TIME_SORT_OPTIONS: { value: HomeTimeSortOption; label: string }[] = [
  { value: "time", label: "按时间排序" },
  { value: "created", label: "按创建时间" },
  { value: "updated", label: "按更新时间" },
];

/** 仅管理员可见（showScopeFilter）；无「全部」「我的剧集」，第二项为普通用户维度剧集 */
const SCOPE_FILTER_OPTIONS: { value: HomeScopeOption; label: string }[] = [
  { value: "team", label: "团队剧集" },
  { value: "mine", label: "普通用户剧集" },
];

export interface HomeSeriesContentProps {
  /**
   * 为 true 时仅渲染列表主区（工具栏+网格+分页），顶栏与通知由 PremiumWorkspaceLayout 提供。
   */
  embeddedInWorkspace?: boolean;
  totalCount: number;
  pageItems: HomeSeriesFolderItem[];
  page: number;
  pageCount: number;
  hasPrev: boolean;
  hasNext: boolean;
  isLoading: boolean;
  searchInputValue: string;
  onSearchChange: (value: string) => void;
  timeSort: HomeTimeSortOption;
  onTimeSortChange: (value: HomeTimeSortOption) => void;
  scope: HomeScopeOption;
  onScopeChange: (value: HomeScopeOption) => void;
  showScopeFilter: boolean;
  onPagePrev: () => void;
  onPageNext: () => void;
  onFolderOpen: (item: HomeSeriesFolderItem) => void;
  onNewSeries: () => void;
  onRename: (item: HomeSeriesFolderItem) => void;
  onDelete: (item: HomeSeriesFolderItem) => void;
  canCreateSeries: boolean;
  canDeleteSeries: boolean;
}

export const HomeSeriesContent: FC<HomeSeriesContentProps> = ({
  embeddedInWorkspace = false,
  totalCount,
  pageItems,
  page,
  pageCount,
  hasPrev,
  hasNext,
  isLoading,
  searchInputValue,
  onSearchChange,
  timeSort,
  onTimeSortChange,
  scope,
  onScopeChange,
  showScopeFilter,
  onPagePrev,
  onPageNext,
  onFolderOpen,
  onNewSeries,
  onRename,
  onDelete,
  canCreateSeries,
  canDeleteSeries,
}) => {
  const listBody = (
    <>
        <div className="home-series__toolbar">
          <div className="home-series__filters">
            <div className="home-series__search-wrap">
              <span className="home-series__search-icon">
                <SearchOutlined />
              </span>
              <input
                type="search"
                className="home-series__search"
                placeholder="搜索剧集..."
                value={searchInputValue}
                onChange={(e) => onSearchChange(e.target.value)}
                aria-label="搜索剧集"
              />
            </div>
            <HomeToolbarSelect
              widthPreset="time"
              value={timeSort}
              onChange={(v) => onTimeSortChange(v as HomeTimeSortOption)}
              options={TIME_SORT_OPTIONS}
              aria-label="时间排序"
            />
            {showScopeFilter && (
              <HomeToolbarSelect
                widthPreset="scope"
                value={scope}
                onChange={(v) => onScopeChange(v as HomeScopeOption)}
                options={SCOPE_FILTER_OPTIONS}
                aria-label="剧集范围"
              />
            )}
          </div>
          {canCreateSeries && (
            <button type="button" className="home-series__btn-primary" onClick={onNewSeries}>
              <PlusOutlined />
              <span>新建剧集</span>
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="home-series__empty">加载中…</div>
        ) : pageItems.length === 0 ? (
          <div className="home-series__empty">暂无剧集</div>
        ) : (
          <div className="home-series__grid">
            {pageItems.map((item) => (
              <div
                key={item.id}
                className="home-series__card"
                role="link"
                tabIndex={0}
                onClick={() => onFolderOpen(item)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onFolderOpen(item);
                  }
                }}
              >
                <div className="home-series__card-thumb">
                  <img
                    className="home-series__card-thumb-img"
                    src={SERIES_FOLDER_THUMB_URL}
                    alt=""
                    width={64}
                    height={64}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="home-series__card-foot">
                  <h3 className="home-series__card-name" title={item.name || undefined}>
                    {item.name}
                  </h3>
                  <div className="home-series__card-actions">
                    <button
                      type="button"
                      className="home-series__icon-btn"
                      title="重命名"
                      aria-label={`重命名 ${item.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRename(item);
                      }}
                    >
                      <EditOutlined style={{ fontSize: 14 }} />
                    </button>
                    {canDeleteSeries ? (
                      <Popconfirm
                        title="确定要删除这个剧集吗？"
                        okText="确定"
                        cancelText="取消"
                        onPopupClick={(e) => {
                          e.stopPropagation();
                        }}
                        onConfirm={() => onDelete(item)}
                      >
                        <button
                          type="button"
                          className="home-series__icon-btn home-series__icon-btn--danger"
                          title="删除"
                          aria-label={`删除 ${item.name}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <DeleteOutlined style={{ fontSize: 14 }} />
                        </button>
                      </Popconfirm>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {totalCount > 0 && (
          <HomeSeriesPagination
            page={page}
            pageCount={pageCount}
            hasPrev={hasPrev}
            hasNext={hasNext}
            onPagePrev={onPagePrev}
            onPageNext={onPageNext}
          />
        )}
    </>
  );

  if (embeddedInWorkspace) {
    return listBody;
  }

  return (
    <div className="home-series">
      <div className="home-series__scroll">{listBody}</div>
    </div>
  );
};
